// A streamed app's shortcuts around its window (docs/features/app-shortcuts.md): shown when an on extension's
// `shortcuts.apps` lists the stream's bundle id. A small glass strip on the trailing side of the picture — a Shortcuts
// button (opens the sheet, sheet.js) and one button per pinned shortcut — and the gestures on the stream's bar (its
// title in the control bar, never the picture, where a hold and a double tap already mean right-click and
// double-click): a long press on the title is `pinchHold`, a double tap on it `doubleTap`. The two-finger swipes have
// no touch of their own here — two fingers on the scene are the viewer's zoom and pan (core/view.js) — so a swipe
// with a shortcut assigned is a labeled button in the strip ("← Back"). A gesture with nothing assigned does nothing.
//
// A tap or a gesture sends the key down and up into this stream's input (send.js) — only what the person did.
// The `sheet` action (a part button's `action`, a ⋯ item's id) opens the sheet here instead of going to the Mac.
//
// One seam, used by macwindow.js (and stream-parts.js for the `sheet` action): `new StreamShortcuts(win)` →
// `strip()`, `barAttrs()`, `overlay()` (HTML); `act`, `hold`, `typed`, `after`; `open`, `shown`, `minSize()`;
// `show({section, focus})`; `dispose()`.
import { esc, icon } from '../core/kit.js'
import { Extensions } from '../extensions/extensions.js'
import { Shortcuts, stripItems, itemOf, gestureOf, SWIPES } from './store.js'
import { ShortcutSheet, shortcutStyles, opOf } from './sheet.js'
import { sendToStream } from './send.js'

const DOUBLE_TAP = 400   // ms between the two taps on the title
const img = (symbol, colour = 'var(--c-ink)') => `<img class="sym" src="${icon(symbol, colour)}">`

export class StreamShortcuts {
  constructor(win) {
    this.win = win
    this.followed = null   // {mac, key}: what the Mac was asked to keep telling
    this.buttons = []      // the strip's buttons, as drawn
    this.shown = false     // the strip is in this drawing
    this.lastTap = 0
    const me = this
    this.sheet = new ShortcutSheet({
      get macKey() { return me.macKey },
      get key() { return me.bundle },
      get title() { return me.win.stream?.window?.app || '' },
      get offered() { return !!me.offered },
      get keys() { return me.canSend },
      sections: () => me.sections(),
      run: (item) => me.send(item),
      action: (ext, a) => me.action(ext, a),
      redraw: () => me.win.render(),
      gestureHelp: 'Here, a gesture is made on this window’s title: hold it for Pinch and Hold, double-tap it for Double Tap. A swipe shows as a button beside the window.',
    })
    shortcutStyles.then(() => win.render())
  }
  get macKey() { return this.win.ctx.macID || this.win.ctx.macKey || null }
  get bundle() { const w = this.win.stream?.window; return w?.bundleID || w?.bundleId || '' }
  /// The on extension that offers shortcuts for this app, or null.
  get offered() { return Shortcuts.forApp(this.macKey, this.bundle) }
  get set() { return this.offered ? Shortcuts.get(this.macKey, this.bundle) : null }
  get canSend() { return this.win.stream?.canControl !== false }
  get open() { return this.sheet.open }

  /// Ask when the stream shows an app with shortcuts; again on a new wire; stop when it no longer does.
  track() {
    const mac = this.macKey, key = this.offered ? this.bundle : null, f = this.followed
    if (f && (f.key !== key || f.mac !== mac)) { Shortcuts.unfollow(f.mac, f.key); this.followed = null }
    if (!key) return
    if (!this.followed) { this.followed = { mac, key }; Shortcuts.follow(mac, key) } else Shortcuts.ensure(mac, key)
  }

  // MARK: drawing

  /// The strip beside the picture ('' when no extension offers shortcuts for this app, or the stream is view only).
  strip() {
    this.track()
    this.shown = !!this.offered && this.canSend
    this.buttons = this.shown ? stripItems(this.set, SWIPES) : []
    if (!this.shown) return ''
    const app = this.win.stream?.window?.app || 'this app'
    const one = (b, i) => `<button class="sc-b${b.item.enabled ? '' : ' off'}" data-act="sc:fire~${i}" data-help="${esc([b.item.id, b.item.keys].filter(Boolean).join(' — '))}">` +
      (b.gestures.length ? `<span class="sc-g">${esc(b.gestures.map((g) => g.glyph).join(''))}</span>` : '') + `<span class="sc-bt">${esc(b.item.title)}</span></button>`
    return `<div class="sc-strip"><div class="sc-col" data-scroll="sc-strip" style="max-height:${this.win.picH}px">` +
      `<button class="sc-b sc-open" data-act="sc:open" data-help="Every keyboard shortcut of ${esc(app)} as a button: tap one to press it, star it to keep it here, hold it to give it a gesture.">${img('command')}<span class="sc-bt">Shortcuts</span></button>` +
      this.buttons.map(one).join('') + '</div></div>'
  }
  /// The title in the control bar takes the gestures: what makes it tappable, and what it says when rested on.
  barAttrs() {
    if (!this.offered || !this.canSend) return ''
    const set = this.set
    const says = ['pinchHold', 'doubleTap'].map((n) => { const it = itemOf(set, set?.gestures?.[n]); return it ? `${n === 'pinchHold' ? 'Hold' : 'Double-tap'}: ${it.title}` : null }).filter(Boolean)
    return ` data-act="scbar:"${says.length ? ` data-help="${esc(says.join(' · '))}"` : ''}`
  }
  overlay() { return this.sheet.html() }
  /// The plane's least size while the sheet is open, so a small window still has room for it.
  minSize() { return this.sheet.open ? { w: 600, h: 640 } : null }
  after() { this.sheet.after(this.win.root) }

  // MARK: what the sheet shows and does

  /// The docs' `sheet` sections for this window, each with the extension it came from.
  sections() {
    const s = this.win.xs?.cached || Extensions.streamOf(this.macKey, this.win.stream?.window)
    return (s?.sources || []).flatMap((src) => (Array.isArray(src.doc?.sheet) ? src.doc.sheet : []).map((sec) => ({ ext: src.ext, title: sec?.title || '', items: sec?.items || [] })))
  }
  /// The sheet, opened by the Shortcuts button or by the `sheet` action (`args.section`, the search focused).
  show({ section = null, focus = false, pop = null } = {}) { this.sheet.show({ section, focus, pop }) }
  /// One of the extension's named actions: asked first when it says `confirm`, as stream-parts.js asks.
  action(ext, a) {
    const id = a.action ?? a.id
    if (id == null) return
    if (id === 'sheet') { this.show({ section: a.args?.section ?? null, focus: true }); return }
    const xs = this.win.xs
    if (a.confirm) { xs.ps(ext).confirm = { action: id, args: a.args, confirm: a.confirm, title: a.title }; return }
    xs.handler(ext).action({ action: id, args: a.args || {} })
  }
  /// A shortcut, as the person pressing its keys in this stream.
  send(item) {
    if (!item || !this.canSend) return false
    if (this.set?.to === 'terminal') { this.win.ctx.note?.('That shortcut is for a terminal, not a streamed window.'); return false }
    if (!sendToStream(this.win.stream, item)) { this.win.ctx.note?.(`The Mac has no key for ${item.keys || item.title}.`); return false }
    return true
  }
  /// A gesture made on the bar: its shortcut, if one is assigned. False when nothing is.
  gesture(name) {
    const set = this.set, item = gestureOf(name) && itemOf(set, set?.gestures?.[name])
    return item ? this.send(item) : false
  }

  // MARK: taps

  act(kind, arg, el) {
    if (kind === 'scbar') {
      const now = performance.now()
      if (now - this.lastTap < DOUBLE_TAP) { this.lastTap = 0; this.gesture('doubleTap') } else this.lastTap = now
      return false
    }
    if (kind !== 'sc') return undefined
    const [op, a] = opOf(arg)
    if (op === 'open') { this.show(); return true }
    if (op === 'fire') { const b = this.buttons[+a]; if (b) this.send(b.item); return false }
    return this.sheet.act(kind, arg, el)
  }
  /// A long press: on the title it is `pinchHold`; on a tile, or a strip button, the context menu (Assign Gesture…).
  hold(kind, arg) {
    // (the press that ends a hold is never a tap towards a double tap, whether or not a shortcut is assigned)
    if (kind === 'scbar') { this.lastTap = 0; this.gesture('pinchHold'); return true }
    if (kind !== 'sc') return false
    const [op, a] = opOf(arg)
    if (op === 'fire') {
      const b = this.buttons[+a]
      if (!b) return false
      this.show({ pop: { id: b.item.id, step: 'menu' } })
      this.win.render()
      return true
    }
    return this.sheet.hold(kind, arg)
  }
  typed(key, value) { return this.sheet.typed(key, value) }

  dispose() { const f = this.followed; if (f) { Shortcuts.unfollow(f.mac, f.key); this.followed = null } }
}
